import * as styles from "./Background.css";

import { animate } from "animejs";
import type { JSAnimation } from "animejs";
import { useEffect, useId, useMemo, useRef } from "react";
import type { JSX } from "react";

import type { BackgroundBeamsProps } from "./Background.types";
import { BEAMS_VIEWBOX_SIZE, buildBeams } from "./beams";

// The lit segment is a window sliding along the gradient, from just before
// the path to just past it.
const WINDOW_START = "-30%";
const WINDOW_END = "130%";
const TRAVEL_START = "0%";
const TRAVEL_END = "100%";

const RAIL_OPACITY = 0.12;

export function BackgroundBeams({
  color,
  count,
  duration,
  intensity,
  beamWidth,
  rails,
}: BackgroundBeamsProps): JSX.Element {
  const gradientId = useId();
  const svgRef = useRef<SVGSVGElement>(null);

  const beams = useMemo(() => buildBeams(count, duration), [count, duration]);

  useEffect(() => {
    const svg = svgRef.current;

    if (!svg) {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const animations: JSAnimation[] = [];

    beams.forEach((beam, index) => {
      const gradient = svg.querySelector<SVGLinearGradientElement>(
        `#${CSS.escape(`${gradientId}-${index}`)}`,
      );

      if (!gradient) {
        return;
      }

      animations.push(
        animate(gradient, {
          x1: [TRAVEL_START, WINDOW_END],
          x2: [WINDOW_START, TRAVEL_END],
          y1: [TRAVEL_START, WINDOW_END],
          y2: [WINDOW_START, TRAVEL_END],
          duration: beam.durationMs,
          delay: beam.delayMs,
          ease: "linear",
          loop: true,
        }),
      );
    });

    return () => {
      animations.forEach((animation) => animation.revert());
    };
  }, [beams, gradientId]);

  return (
    <svg
      ref={svgRef}
      className={styles.canvas}
      viewBox={`0 0 ${BEAMS_VIEWBOX_SIZE} ${BEAMS_VIEWBOX_SIZE}`}
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
      style={{ opacity: intensity }}
    >
      {rails &&
        beams.map((beam, index) => (
          <path
            key={`rail-${index}`}
            d={beam.d}
            stroke={color}
            strokeOpacity={RAIL_OPACITY}
            strokeWidth={beamWidth}
            vectorEffect="non-scaling-stroke"
          />
        ))}

      {beams.map((beam, index) => (
        <path
          key={`beam-${index}`}
          d={beam.d}
          stroke={`url(#${gradientId}-${index})`}
          strokeWidth={beamWidth}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      <defs>
        {beams.map((_, index) => (
          <linearGradient
            key={`gradient-${index}`}
            id={`${gradientId}-${index}`}
            x1={TRAVEL_START}
            x2={WINDOW_START}
            y1={TRAVEL_START}
            y2={WINDOW_START}
          >
            <stop stopColor={color} stopOpacity="0" />
            <stop offset="0.5" stopColor={color} />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
    </svg>
  );
}
